import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { SystemHealthService, type PlatformHealthStatus } from './SystemHealthService';

/** How often the overview re-probes service health while the page is open. */
const REFRESH_MS = 30_000;

/**
 * Loads and periodically refreshes live platform health for System > Overview.
 * The first load shows a loading state; subsequent refreshes update in place and
 * keep the last good snapshot if a refresh fails, so the view doesn't flicker.
 */
@injectable()
export class SystemHealthViewModel extends BaseViewModel {
    data: PlatformHealthStatus | null = null;
    loading = true;
    error: string | null = null;
    private initialized = false;
    private timer: ReturnType<typeof setInterval> | null = null;

    constructor(private readonly service: SystemHealthService) {
        super();
    }

    init(): void {
        if (this.initialized) return;
        this.initialized = true;
        void this.load();
        this.timer = setInterval(() => void this.load(), REFRESH_MS);
    }

    async load(): Promise<void> {
        try {
            this.data = await this.service.getHealth();
            this.error = null;
        } catch (e) {
            if (!this.data) this.error = e instanceof Error ? e.message : 'Failed to load system health';
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    dispose(): void {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
        super.dispose();
    }
}
